import { getCoverImageUrl } from '@/utils/mediaUtils';
import { tokenService } from '@/utils/tokenService';
import { ApiArtist } from './artist_types';
import {
  ApiResponse,
  CommonQueryParams,
  DownloadFormat,
  FlexibleApiResponse,
  TrackStatus
} from './common_types';

// ========================================
// API 응답 타입
// ========================================

/**
 * 트랙 기본 정보
 */
export interface ApiAudioTrack {
  track_number: number;
  title: string;
  nft_name?: string;
  audio_file: string;
  stream_url?: string;
  preview_url?: string;
  cover_image?: string;
  duration: string; // 백엔드에서 "HH:MM:SS" 형식 문자열로 옴
  price_krw: string;
  supply_total: number;
  supply_sold: number;
  sale_start: string;
  sale_end: string;
  royalty_recipient?: string;
  royalty_percentage?: number;
  status: TrackStatus;
  is_purchased?: boolean;
  is_cart_item?: boolean;
  contract_address?: string;
  network?: string;
  contract_published_at?: string | null;
  minter_address?: string;
  remaining_downloads?: number;
  downloads_per_nft?: number;
  file_size?: number;
  artist?: ApiArtist;
  genre?: string;
  created_at?: string;
}


// ========================================
// API 요청 파라미터
// ========================================

/**
 * 아티스트 목록 요청 파라미터
 */
export interface ArtistParams extends CommonQueryParams {
  // CommonQueryParams에 이미 page, page_size, ordering, search 포함
}

/**
 * 트랙 목록 요청 파라미터
 */
export interface TrackListParams extends CommonQueryParams {
  status?: TrackStatus;
  artists?: string;
}

// ========================================
// API 응답 타입 (common_types 활용)
// ========================================

/**
 * 트랙 목록 API 응답
 * - ApiResponse<ApiAudioTrack[]> 또는
 * - ApiResponse<PaginatedData<ApiAudioTrack>>
 */
export type TrackListResponse = FlexibleApiResponse<ApiAudioTrack>;

/**
 * 트랙 상세 조회 응답
 */
export type TrackDetailResponse = ApiResponse<ApiAudioTrack>;

// ========================================
// 앱 내부 사용 타입 (정규화된 타입)
// ========================================

/**
 * 오디오 플레이어에서 사용하는 트랙 정보
 * API 응답을 정규화한 최소한의 데이터
 */
export interface PlayerTrack {
  id: string;
  title: string;
  artistId: number;
  artistName: string;
  coverImage: string;
  audioUrl: string;
  stream_url?: string;
  preview_url?: string;
  duration: string; // "HH:MM:SS" 형식 문자열 (UI 표시용)
  isPurchased?: boolean;
  isInCart?: boolean;
  status?: TrackStatus;
  isSelected?: boolean;
}

/**
 * UI 카드에서 사용하는 트랙 정보
 * PlayerTrack + 판매/NFT 정보
 */
export interface CardTrack extends PlayerTrack {
  priceKrw: number;
  nft: {
    totalSupply: number;
    soldCount: number;
  };
  saleInfo: {
    startDate: string;
    endDate: string;
    status: TrackStatus;
  };
  fileSize?: number;
  // 마이페이지용 선택적 필드
  isInMyWallet?: boolean;
  purchaseInfo?: PurchaseInfo;
}

/**
 * 구매 정보
 */
export interface PurchaseInfo {
  purchaseId: string;
  purchaseDate: string;
  remainingDownloads: number;
  maxDownloads: number;
  transactionId: string;
}

/**
 * 상품 상세 페이지에서 사용하는 확장된 트랙 정보
 */
export interface DetailTrack extends CardTrack {
  nftName?: string;
  nftId?: string;
  network?: string;
  contractAddress?: string;
  downloadsPerNft?: number;
}

/**
 * 다운로드 정보
 */
export interface DownloadInfo {
  title: string;
  artist: string;
  format: DownloadFormat;
  fileSize: string;
  remainingDownloads: number;
  totalDownloads: number;
}

// ========================================
// 타입 변환 유틸리티 함수
// ========================================

/**
 * API 응답을 PlayerTrack으로 변환
 * @param apiTrack - API에서 받은 트랙 데이터
 * @param isPurchased - 구매 여부 (기본값: false)
 * @returns 플레이어용으로 정규화된 트랙 데이터
 */
export function toPlayerTrack(apiTrack: ApiAudioTrack, isPurchased = false): PlayerTrack {
  // 백엔드 스트리밍 API 엔드포인트 사용: /api/tracks/{track_number}/stream/
  // HTML5 Audio 요소는 커스텀 헤더를 설정할 수 없으므로 JWT 토큰을 쿼리 파라미터로 전달
  const apiUrl = import.meta.env.VITE_API_URL || '';
  const baseStreamUrl = `${apiUrl}/tracks/${apiTrack.track_number}/stream/`;
  const accessToken = tokenService.getAccessToken();
  const streamUrl = accessToken
    ? `${baseStreamUrl}?token=${encodeURIComponent(accessToken)}`
    : baseStreamUrl;

  return {
    id: apiTrack.track_number.toString(),
    title: apiTrack.title,
    artistId: apiTrack.artist?.artist_number || 0,
    artistName: apiTrack.artist?.display_name || 'Unknown Artist',
    coverImage: getCoverImageUrl(apiTrack.cover_image),
    audioUrl: streamUrl,
    duration: apiTrack.duration || '00:00:00',
    isPurchased: apiTrack.is_purchased ?? isPurchased,
    isInCart: apiTrack.is_cart_item ?? false,
    status: apiTrack.status,
  };
}

/**
 * API 응답을 CardTrack으로 변환
 * @param apiTrack - API에서 받은 트랙 데이터
 * @param isPurchased - 구매 여부 (기본값: false)
 * @param purchaseInfo - 구매 정보 (선택사항)
 * @returns 카드용으로 정규화된 트랙 데이터
 */
export function toCardTrack(apiTrack: ApiAudioTrack, isPurchased = false, purchaseInfo?: PurchaseInfo): CardTrack {
  const playerTrack = toPlayerTrack(apiTrack, isPurchased);

  return {
    ...playerTrack,
    priceKrw: parseInt(apiTrack.price_krw) || 0,
    nft: {
      totalSupply: apiTrack.supply_total,
      soldCount: apiTrack.supply_sold,
    },
    saleInfo: {
      startDate: apiTrack.sale_start,
      endDate: apiTrack.sale_end,
      status: apiTrack.status,
    },
    fileSize: apiTrack.file_size,
    isPurchased: apiTrack.is_purchased ?? isPurchased,
    isInCart: apiTrack.is_cart_item ?? false,
    purchaseInfo,
  };
}

/**
 * API 응답을 DetailTrack으로 변환
 * @param apiTrack - API에서 받은 트랙 데이터
 * @param isPurchased - 구매 여부 (기본값: false)
 * @returns 상세 페이지용으로 정규화된 트랙 데이터
 */
export function toDetailTrack(apiTrack: ApiAudioTrack, isPurchased = false): DetailTrack {
  const cardTrack = toCardTrack(apiTrack, isPurchased);

  return {
    ...cardTrack,
    nftName: apiTrack.nft_name,
    network: apiTrack.network,
    contractAddress: apiTrack.contract_address,
    downloadsPerNft: apiTrack.downloads_per_nft,
  };
}

// ========================================
// 타입 가드 및 검증 함수
// ========================================

/**
 * 판매 종료 여부 확인
 * @param track - 트랙 정보
 * @returns 판매가 종료되었으면 true
 */
export function isSaleEnded(track: CardTrack): boolean {
  return track.saleInfo.status === 'ENDED' || new Date(track.saleInfo.endDate) < new Date();
}

/**
 * 품절 여부 확인
 * @param track - 트랙 정보
 * @returns 품절되었으면 true
 */
export function isSoldOut(track: CardTrack): boolean {
  return track.saleInfo.status === 'SOLDOUT' || track.nft.soldCount >= track.nft.totalSupply;
}

/**
 * 구매 가능 여부 확인
 * @param track - 트랙 정보
 * @returns 구매 가능하면 true
 */
export function isPurchasable(track: CardTrack): boolean {
  return !isSaleEnded(track) && !isSoldOut(track) && !track.isPurchased;
}
